import { useEffect, useState } from 'react';
import { CalendarPlus, Check, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatLong } from '@/lib/cycle';

interface PlanAheadCardProps {
  /** ISO date (in the future) being planned for. */
  date: string;
  /** Current saved plan text for this day (may be empty). */
  event: string;
  onSaveEvent: (text: string) => void;
  /** True when this date falls inside the predicted flare-up window. */
  isFlareDay: boolean;
  /** Suggested low-pain window, e.g. "Oct 2 – Oct 7", shown on flare days. */
  lightWindow?: string;
}

/**
 * Lets the user attach a plan ("Wedding", "Big presentation") to a future
 * day, and warns immediately when that day is a predicted flare-up day.
 */
export function PlanAheadCard({
  date,
  event,
  onSaveEvent,
  isFlareDay,
  lightWindow,
}: PlanAheadCardProps) {
  const [text, setText] = useState(event);

  useEffect(() => {
    setText(event);
  }, [date, event]);

  const saved = text.trim() === event.trim() && event.trim() !== '';

  return (
    <Card className="rounded-3xl shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl font-bold">Plan ahead · {formatLong(date)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isFlareDay && (
          <div className="flex items-start gap-3 rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-950">
            <TriangleAlert className="mt-0.5 size-5 shrink-0 text-rose-600" aria-hidden />
            <p className="text-sm leading-relaxed">
              <strong>Heads up — this is a predicted flare-up day.</strong> If this plan is
              demanding, consider moving it{lightWindow ? ` to ${lightWindow}` : ''}.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="plan-text" className="text-base font-semibold">
            What&apos;s on this day?
          </label>
          <div className="flex gap-2">
            <Input
              id="plan-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSaveEvent(text);
                }
              }}
              placeholder="e.g. Team meeting, wedding, trip…"
              maxLength={60}
              className="h-14 flex-1 rounded-xl text-lg"
            />
            <Button
              type="button"
              onClick={() => onSaveEvent(text)}
              className="h-14 shrink-0 rounded-2xl px-5 font-bold"
            >
              {saved ? <Check className="size-5" aria-hidden /> : <CalendarPlus className="size-5" aria-hidden />}
              {saved ? 'Saved' : 'Save'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Symptom logging unlocks on the day — plans can be added any time. Clear the text and
            save to remove a plan.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
